import { useState, useEffect } from 'react';
import { createLicence, getAllLicences } from '../api';
import EmptyState from '../components/EmptyState';

export default function LicenceAdminPage() {
  const [licences, setLicences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ totalCredits: '', notes: '' });
  const [createdKey, setCreatedKey] = useState('');
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadLicences = () => {
    getAllLicences()
      .then(setLicences)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadLicences(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setCreatedKey('');
    setCopied(false);
    setCreating(true);
    try {
      const result = await createLicence({
        totalCredits: Number(form.totalCredits),
        notes: form.notes || null,
      });
      setCreatedKey(result.licenceKey);
      setForm({ totalCredits: '', notes: '' });
      loadLicences();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(createdKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="page-container">
      <div className="form-card">
        <h2>Create Licence</h2>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleCreate}>
          <div className="form-row">
            <div className="form-group">
              <label>Credits (reports allowed)</label>
              <input
                type="number"
                min="1"
                value={form.totalCredits}
                onChange={(e) => setForm({ ...form, totalCredits: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Internal Notes</label>
              <input
                type="text"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. Client name, invoice ref"
              />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={creating}>
            {creating ? 'Creating...' : 'Create Licence'}
          </button>
        </form>
        {createdKey && (
          <div className="alert alert-success" style={{ marginTop: '1rem' }}>
            <strong>Licence created!</strong> Key:{' '}
            <code style={{ userSelect: 'all', padding: '2px 6px', background: '#e8e8e8', borderRadius: '3px' }}>
              {createdKey}
            </code>{' '}
            <button type="button" onClick={copyKey} className="btn btn-sm" style={{ marginLeft: '0.5rem' }}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        )}
      </div>

      <div className="form-card" style={{ marginTop: '1.5rem' }}>
        <h2>All Licences</h2>
        {loading ? (
          <p>Loading...</p>
        ) : licences.length === 0 ? (
          <EmptyState message="No licences created yet." />
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Credits</th>
                  <th>Used</th>
                  <th>Remaining</th>
                  <th>Organisation</th>
                  <th>Created By</th>
                  <th>Notes</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {licences.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <code style={{ fontSize: '0.8em', padding: '2px 6px', background: '#f3f4f6', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                        {l.licenceKey}
                      </code>
                    </td>
                    <td style={{ textAlign: 'center' }}>{l.totalCredits}</td>
                    <td style={{ textAlign: 'center' }}>{l.creditsUsed}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600, color: l.creditsRemaining === 0 ? '#dc2626' : '#16a34a' }}>
                      {l.creditsRemaining}
                    </td>
                    <td>{l.organisationName || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Unredeemed</span>}</td>
                    <td>{l.createdByName}</td>
                    <td style={{ color: l.notes ? '#333' : '#9ca3af', maxWidth: '200px' }}>{l.notes || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{new Date(l.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
